import { decodeGoogleRoutesEncodedPolyline } from "@gpx-spa/geo-adapters";
import type {
  RoutePathMeta,
  RoutePathOption,
  RoutePlan,
  RouteSegmentPlan,
} from "@gpx-spa/geo-domain";
import type { ShipmentRouteBatchRow } from "@gpx-spa/queries/src/getAccountShipmentRoutesBatch";

type ApiStopRef = { id?: number; sequence: number };

/**
 * Keeps one batch row per `(from_stop_id, to_stop_id)` — highest `id` wins.
 */
export function dedupeShipmentRouteBatchRows(
  rows: readonly ShipmentRouteBatchRow[],
): ShipmentRouteBatchRow[] {
  const byPair = new Map<string, ShipmentRouteBatchRow>();
  for (const row of rows) {
    const key = `${row.from_stop_id}:${row.to_stop_id}`;
    const prev = byPair.get(key);
    if (!prev || row.id > prev.id) {
      byPair.set(key, row);
    }
  }
  return [...byPair.values()].sort((a, b) => {
    if (a.from_stop_id !== b.from_stop_id) {
      return a.from_stop_id - b.from_stop_id;
    }
    return a.to_stop_id - b.to_stop_id;
  });
}

function stopIndexById(sortedStops: ApiStopRef[]): Map<number, number> {
  const m = new Map<number, number>();
  sortedStops.forEach((s, idx) => {
    if (typeof s.id === "number" && s.id > 0) m.set(s.id, idx);
  });
  return m;
}

function routeOptionToPath(
  opt: ShipmentRouteBatchRow["route_options"][number],
  pathIndex: number,
): RoutePathOption | null {
  const enc = opt?.encoded_polyline;
  if (typeof enc !== "string" || !enc.trim()) return null;
  const coordinates = decodeGoogleRoutesEncodedPolyline(enc);
  if (!coordinates.length || coordinates.length < 2) return null;
  const name =
    typeof opt.name === "string" && opt.name.trim()
      ? opt.name.trim()
      : undefined;
  const rawMeta =
    opt.meta != null && typeof opt.meta === "object"
      ? (opt.meta as RoutePathMeta)
      : undefined;
  const provider = rawMeta?.provider;
  if (provider === "learned") {
    const learnedId =
      name && /^learned_\d+$/.test(name) ? name : `learned_${pathIndex}`;
    return {
      id: learnedId,
      coordinates,
      meta: {
        provider: "learned",
        ...(typeof rawMeta?.distance_m === "number"
          ? { distance_m: rawMeta.distance_m }
          : {}),
        ...(typeof rawMeta?.duration_s === "number"
          ? { duration_s: rawMeta.duration_s }
          : {}),
        ...(typeof rawMeta?.sample_count === "number"
          ? { sample_count: rawMeta.sample_count }
          : {}),
      },
    };
  }
  return {
    id: name ?? undefined,
    coordinates,
    meta: rawMeta,
  };
}

/**
 * Builds a {@link RoutePlan} from device-api `GET …/shipment-routes` rows and v2 stop ids.
 */
export function shipmentRouteBatchRowsToRoutePlan(
  rows: readonly ShipmentRouteBatchRow[],
  apiStops: readonly ApiStopRef[],
): RoutePlan | null {
  const sorted = [...apiStops].sort((a, b) => a.sequence - b.sequence);
  const byId = stopIndexById(sorted);
  if (byId.size < 2) return null;

  const deduped = dedupeShipmentRouteBatchRows(rows);
  const forStops = deduped.filter((r) => {
    return (
      byId.has(r.from_stop_id) &&
      byId.has(r.to_stop_id) &&
      Array.isArray(r.route_options) &&
      r.route_options.length > 0
    );
  });
  if (!forStops.length) return null;

  forStops.sort((a, b) => {
    const ai = byId.get(a.from_stop_id) ?? 0;
    const bi = byId.get(b.from_stop_id) ?? 0;
    if (ai !== bi) return ai - bi;
    const aj = byId.get(a.to_stop_id) ?? 0;
    const bj = byId.get(b.to_stop_id) ?? 0;
    return aj - bj;
  });

  const segments: RouteSegmentPlan[] = [];
  let seq = 0;
  for (const row of forStops) {
    const fromStopIndex = byId.get(row.from_stop_id);
    const toStopIndex = byId.get(row.to_stop_id);
    if (fromStopIndex == null || toStopIndex == null) continue;
    const paths: RoutePathOption[] = [];
    for (let i = 0; i < row.route_options.length; i++) {
      const p = routeOptionToPath(row.route_options[i]!, i);
      if (p) paths.push(p);
    }
    if (!paths.length) continue;
    segments.push({
      sequence: seq++,
      fromStopIndex,
      toStopIndex,
      paths,
    });
  }

  if (!segments.length) return null;

  return {
    segments,
    meta: {
      provider: "device-api",
    },
  };
}
